import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/feature/AdminLayout';
import StatsCard from '../../components/base/StatsCard';
import StatusBadge, { PlanBadge } from '../../components/base/StatusBadge';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { fetchDashboardSummary, type DashboardSummary } from '../../api/dashboard.api';
import { formatPlanMoney } from '../../lib/planCurrency';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<DashboardSummary | null>(null);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError('');
      const r = await fetchDashboardSummary();
      setLoading(false);
      if (r.ok === false) {
        if (r.unauthorized) {
          navigate('/admin/login', { replace: true });
          return;
        }
        setError(r.message);
        return;
      }
      setSummary(r.data);
    })();
  }, [navigate]);

  const revenue = summary?.stats.total_revenue;
  const byCurrency = revenue?.by_currency ?? [];
  const primarySymbol = revenue?.currency_symbol ?? 'ج.م';

  const statsCards = [
    {
      id: 'total_revenue',
      value: loading
        ? '…'
        : byCurrency.length > 1
          ? byCurrency.map((c) => formatPlanMoney(c.total, c.currency)).join(' · ')
          : formatPlanMoney(revenue?.value ?? 0, revenue?.currency),
      change: revenue?.change != null ? `${revenue.change > 0 ? '+' : ''}${revenue.change}%` : '—',
      changeType: (revenue?.change ?? 0) >= 0 ? ('up' as const) : ('down' as const),
      icon: 'ri-money-dollar-circle-line',
      color: 'teal' as const,
    },
    {
      id: 'active_subscriptions',
      value: loading ? '…' : String(summary?.stats.active_subscriptions.value ?? 0),
      change: '—',
      changeType: 'up' as const,
      icon: 'ri-checkbox-circle-line',
      color: 'emerald' as const,
    },
    {
      id: 'new_ateliers',
      value: loading ? '…' : String(summary?.stats.new_ateliers.value ?? 0),
      change: '30 يوم',
      changeType: 'up' as const,
      icon: 'ri-store-2-line',
      color: 'amber' as const,
    },
    {
      id: 'churn_rate',
      value: loading ? '…' : `${summary?.stats.churn_rate.value ?? 0}%`,
      change: '—',
      changeType: 'down' as const,
      icon: 'ri-arrow-down-circle-line',
      color: 'rose' as const,
    },
  ];

  const chartData = (summary?.revenue_chart ?? []).map((row) => ({
    month: row.month,
    revenue: row.primary_total,
  }));

  const growthData = summary?.growth_chart ?? [];

  return (
    <AdminLayout>
      <div className="flex flex-col gap-6">
        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {statsCards.map((stat) => (
            <StatsCard
              key={stat.id}
              statKey={stat.id}
              value={stat.value}
              change={stat.change}
              changeType={stat.changeType}
              icon={stat.icon}
              color={stat.color}
            />
          ))}
        </div>

        {byCurrency.length > 1 ? (
          <div className="bg-white rounded-xl p-4 ring-1 ring-gray-100">
            <p className="text-sm font-bold text-gray-900 mb-3">الإيرادات حسب العملة</p>
            <div className="flex flex-wrap gap-3">
              {byCurrency.map((c) => (
                <div key={c.currency} className="rounded-lg border border-gray-100 px-3 py-2">
                  <p className="text-xs text-gray-500">{c.currency}</p>
                  <p className="text-sm font-bold text-gray-900">{formatPlanMoney(c.total, c.currency)}</p>
                  <p className="text-[11px] text-gray-400">{c.count} دفعة</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
          <div className="xl:col-span-3 bg-white rounded-xl p-5 ring-1 ring-gray-100">
            <h3 className="text-base font-bold text-gray-900">الإيرادات الشهرية</h3>
            <p className="text-xs text-gray-400 mt-0.5">بالعملة الأساسية: {revenue?.currency ?? 'EGP'}</p>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 16, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0d9488" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${primarySymbol}${Number(v).toLocaleString()}`}
                />
                <Tooltip
                  formatter={(value) => [formatPlanMoney(Number(value), revenue?.currency), 'الإيراد']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#0d9488" strokeWidth={2.5} fill="url(#revenueGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="xl:col-span-2 bg-white rounded-xl p-5 ring-1 ring-gray-100">
            <h3 className="text-base font-bold text-gray-900">النمو</h3>
            <p className="text-xs text-gray-400 mt-0.5">اشتراكات وأتيليهات جديدة</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={growthData} margin={{ top: 16, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Legend />
                <Tooltip />
                <Bar dataKey="subscriptions" name="اشتراكات" fill="#0d9488" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ateliers" name="أتيليهات" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h3 className="text-base font-bold text-gray-900">أحدث الاشتراكات</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {(summary?.recent_subscriptions ?? []).length === 0 && !loading ? (
                <p className="px-5 py-6 text-sm text-gray-400">لا توجد اشتراكات بعد.</p>
              ) : null}
              {(summary?.recent_subscriptions ?? []).map((sub) => (
                <div key={sub.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{sub.tenant_name}</p>
                    <p className="text-xs text-gray-400">{sub.created_at ? new Date(sub.created_at).toLocaleDateString('ar-EG') : '—'}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <PlanBadge plan={sub.plan_name} />
                    <span className="text-sm font-bold text-gray-800">
                      {formatPlanMoney(sub.price, sub.currency)}
                    </span>
                    <StatusBadge status={sub.status} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl ring-1 ring-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h3 className="text-base font-bold text-gray-900">أحدث المدفوعات</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {(summary?.recent_payments ?? []).length === 0 && !loading ? (
                <p className="px-5 py-6 text-sm text-gray-400">لا توجد مدفوعات بعد.</p>
              ) : null}
              {(summary?.recent_payments ?? []).map((p) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{p.tenant_name}</p>
                    <p className="text-xs text-gray-400">{p.plan_name}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-800">
                      {formatPlanMoney(p.amount, p.currency)}
                    </span>
                    <StatusBadge status={p.status} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
